import React, { FC } from 'react';
import Icon from '@minecraft.icon';
import { BasicContainer, BodyTextSmall, FlexCol, FlexRow, H3Text, H1Text } from '@minecraft.atoms';
import { useTranslation } from '@blocs.i18n';
import { PlanType } from '../usePlans';
import { FeatureList } from './FeatureList';
import { PlanPriceSection } from './PlanPriceSection';
import { PlanButton } from './PlanButton';
import { generateUniqueId } from '../helpers/functions';
import { PlanSmProps } from './PlanSm';

export const PlanSmV2: FC<PlanSmProps> = React.memo(
  ({ plans, countryCode, setSelectedPlan, onNext, paywallLocation, isAnnual }) => {
    const showablePlans = plans?.filter((plan) => !plan?.shouldHidePlan);
    const { t } = useTranslation();

    const handleNext = (plan: PlanType) => {
      setSelectedPlan(plan);
      onNext();
    };

    return (
      <FlexCol className="cn_w-full cn_atom_pt-3" gap="2">
        {showablePlans.map((plan) => {
          const uniqueId = generateUniqueId({ plan, paywallLocation });
          return (
            <FlexCol
              className="cn_relative"
              gap="8"
              key={plan?.billingCode}
              data-testid={`plan-option-container-${plan.billingCode}`}
            >
              <FlexCol
                justifyContent="center"
                borderRadius="sm"
                borderSize={0.15}
                borderColor="transparent"
                borderStyle="solid"
                borderPlacement="all"
                bgColor="pageBody"
                className="cn_atom_rounded-lg cn_shadow-md cn_w-full cn_atom_p-4 cn_atom_pb-6"
                gap="0"
                key={plan.billingCode}
              >
                {plan.isTrialPeriodEnabled && (
                  <BasicContainer
                    className="cn_absolute cn_atom_py-1 cn_atom_px-3"
                    top={0}
                    left={0}
                    bgColor="paywallWarm"
                    customBordersRadius={{
                      topLeft: 0.3,
                      topRight: 0,
                      bottomLeft: 0,
                      bottomRight: 0.3,
                    }}
                  >
                    <BodyTextSmall color="inverted" fontWeight="medium">
                      {t('common:paywall.plan.trial.freePeriod', { trialLengthDays: plan.trialLengthDays })}
                    </BodyTextSmall>
                  </BasicContainer>
                )}
                <FlexCol className="cn_w-full cn_atom_pb-3 cn_mt-8" borderPlacement="bottom" borderColor="default">
                  <FlexRow alignItems="flex-start" justifyContent="center" gap="2">
                    {plan.icon && <Icon name={plan.icon} themeColor={`${plan.temperature}Paywall`} />}
                    <H1Text>{plan.title}</H1Text>
                  </FlexRow>
                  <PlanPriceSection
                    isAnnual={isAnnual}
                    countryCode={countryCode}
                    alignItems="left"
                    textAlign="left"
                    title={t('common:paywall.plan.trial.freePeriod', { trialLengthDays: plan.trialLengthDays })}
                    isTrialPeriodEnabled={plan.isTrialPeriodEnabled}
                    billingCode={plan.billingCode}
                    billed={plan.billed}
                    currencyCode={plan.currencyCode}
                    planTermCode={plan.term?.code}
                    price={Number(plan.price)}
                  />
                </FlexCol>
                <FeatureList features={plan.keyFeatures} temperature={plan.temperature} isFullFeatureList />
                <PlanButton plan={plan} t={t} goToNextStep={handleNext} uniqueId={uniqueId} />
                {plan?.isTrialPeriodEnabled && (
                  <BodyTextSmall color="disabled" align="center" className="cn_atom_pt-5">
                    {t('common:paywall.plan.trial.legal.cancelTime', {
                      trialLengthDays: plan?.trialLengthDays ?? 30,
                    })}
                  </BodyTextSmall>
                )}
              </FlexCol>
              <FlexCol className="cn_w-full" gap="2">
                <BasicContainer borderPlacement="bottom" borderColor="default" className="cn_w-full cn_atom_pb-3">
                  <H3Text>{t('common:paywall.plan.features.viewFeatures')}</H3Text>
                </BasicContainer>
                <FeatureList features={plan.features} temperature={plan.temperature} isFullFeatureList />
              </FlexCol>
            </FlexCol>
          );
        })}
      </FlexCol>
    );
  }
);
